import { ref, computed } from 'vue'
import { APP_VERSION } from '../constants/appVersion'

export type AnnouncementType = 'info' | 'warning' | 'alert' | 'success'

export interface SystemAnnouncement {
  id: string
  title?: string
  message: string
  type: AnnouncementType
  active: boolean
  startDate?: string // e.g. "2026-09-30T00:00"
  endDate?: string   // e.g. "2026-10-05T23:59"
  targetVersions?: string[] // e.g. ['0.0.100', '0.0.99'], empty means all versions
  dismissible?: boolean // true if user can dismiss it on their device
  actionLabel?: string // optional button/link label e.g. "Details ansehen"
  actionUrl?: string   // optional button/link destination e.g. "/settings" or "https://..."
  createdAt: number
  updatedAt: number
}

// Global reactive state shared across all components
const announcements = ref<SystemAnnouncement[]>([])
const dismissedTimestamps = ref<Map<string, number>>(new Map())
const dismissedIds = computed(() => new Set(dismissedTimestamps.value.keys()))
const isListenerActive = ref(false)
const isLoadingAnnouncements = ref(false)
let unsubscribeSnapshot: (() => void) | null = null

export const unsubscribeAnnouncements = () => {
  if (unsubscribeSnapshot) {
    unsubscribeSnapshot()
    unsubscribeSnapshot = null
    isListenerActive.value = false
  }
}

const DISMISS_STORAGE_KEY_PREFIX = 'dismissed_announcement_'

/**
 * Checks whether an announcement date range is valid for the given timestamp
 */
export function isAnnouncementDateValid(announcement: SystemAnnouncement, now = Date.now()): boolean {
  if (announcement.startDate) {
    const startMs = new Date(announcement.startDate).getTime()
    if (!isNaN(startMs) && now < startMs) {
      return false
    }
  }

  if (announcement.endDate) {
    let endStr = announcement.endDate
    // If only date provided (YYYY-MM-DD), count until end of that day
    if (endStr.length === 10) {
      endStr += 'T23:59:59'
    }
    const endMs = new Date(endStr).getTime()
    if (!isNaN(endMs) && now > endMs) {
      return false
    }
  }

  return true
}

/**
 * Checks whether an announcement matches the user's current app version
 */
export function isAnnouncementVersionMatching(
  announcement: SystemAnnouncement,
  currentVersion = APP_VERSION
): boolean {
  const versions = announcement.targetVersions
  if (!versions || versions.length === 0 || versions.includes('all')) {
    return true
  }
  return versions.includes(currentVersion)
}

/**
 * Checks if an announcement is currently dismissed for this device.
 * If the announcement was updated AFTER the dismissal timestamp, it is considered active again.
 */
export function isAnnouncementDismissed(
  announcement: SystemAnnouncement,
  dismissedMap: Map<string, number> = dismissedTimestamps.value
): boolean {
  if (!announcement.dismissible) return false
  const dismissedAt = dismissedMap.get(announcement.id)
  if (!dismissedAt) return false

  // If announcement was modified after dismissal, show it again
  const lastUpdated = announcement.updatedAt || announcement.createdAt || 0
  if (lastUpdated > dismissedAt) {
    return false
  }

  return true
}

/**
 * Loads dismissed announcement timestamps from localStorage
 */
function loadDismissedTimestamps(): Map<string, number> {
  const map = new Map<string, number>()
  if (typeof window === 'undefined' || !window.localStorage) return map

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(DISMISS_STORAGE_KEY_PREFIX)) {
        const id = key.substring(DISMISS_STORAGE_KEY_PREFIX.length)
        if (id) {
          const rawVal = localStorage.getItem(key)
          const ts = rawVal ? parseInt(rawVal, 10) : Date.now()
          map.set(id, isNaN(ts) ? Date.now() : ts)
        }
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return map
}

/**
 * Cleans up orphaned dismissals in localStorage and memory for announcements that no longer exist
 */
function cleanupOrphanedDismissals(validAnnouncements: SystemAnnouncement[]) {
  if (typeof window === 'undefined' || !window.localStorage) return
  const validIds = new Set(validAnnouncements.map((a) => a.id))

  let hasChanged = false
  const updatedMap = new Map(dismissedTimestamps.value)

  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key && key.startsWith(DISMISS_STORAGE_KEY_PREFIX)) {
        const id = key.substring(DISMISS_STORAGE_KEY_PREFIX.length)
        if (id && !validIds.has(id)) {
          localStorage.removeItem(key)
          updatedMap.delete(id)
          hasChanged = true
        }
      }
    }
  } catch {}

  if (hasChanged) {
    dismissedTimestamps.value = updatedMap
  }
}

/**
 * Removes undefined fields from objects to prevent Firestore serialization issues
 */
function sanitizeAnnouncement(item: SystemAnnouncement): SystemAnnouncement {
  const cleaned: any = {}
  for (const [key, val] of Object.entries(item)) {
    if (val !== undefined) {
      cleaned[key] = val
    }
  }
  return cleaned as SystemAnnouncement
}

export function useAnnouncements() {
  // Initialize dismissed timestamps map if empty
  if (dismissedTimestamps.value.size === 0 && typeof window !== 'undefined') {
    dismissedTimestamps.value = loadDismissedTimestamps()
  }

  /**
   * Filtered active announcements for the current user, matching date range and app version
   */
  const activeAnnouncements = computed(() => {
    const now = Date.now()
    return announcements.value.filter((item) => {
      if (!item.active) return false
      if (!isAnnouncementDateValid(item, now)) return false
      if (!isAnnouncementVersionMatching(item, APP_VERSION)) return false
      if (isAnnouncementDismissed(item, dismissedTimestamps.value)) return false
      return true
    })
  })

  /**
   * Top-most announcement to display in the main top banner
   */
  const currentBannerAnnouncement = computed(() => {
    if (activeAnnouncements.value.length === 0) return null
    // Prioritize alert > warning > info > success, then newest
    const priorityWeight: Record<AnnouncementType, number> = {
      alert: 4,
      warning: 3,
      info: 2,
      success: 1
    }
    return [...activeAnnouncements.value].sort((a, b) => {
      const pDiff = (priorityWeight[b.type] || 1) - (priorityWeight[a.type] || 1)
      if (pDiff !== 0) return pDiff
      return (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0)
    })[0] || null
  })

  /**
   * Dismisses an announcement locally on this device
   */
  const dismissAnnouncement = (id: string) => {
    const now = Date.now()
    const updated = new Map(dismissedTimestamps.value)
    updated.set(id, now)
    dismissedTimestamps.value = updated

    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(`${DISMISS_STORAGE_KEY_PREFIX}${id}`, String(now))
      } catch {
        // Ignore localStorage quota
      }
    }
  }

  /**
   * Clears dismissed status for a specific announcement (e.g. on delete or testing)
   */
  const undismissAnnouncement = (id: string) => {
    if (dismissedTimestamps.value.has(id)) {
      const updated = new Map(dismissedTimestamps.value)
      updated.delete(id)
      dismissedTimestamps.value = updated
    }
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(`${DISMISS_STORAGE_KEY_PREFIX}${id}`)
      } catch {}
    }
  }

  /**
   * Initializes real-time Firestore listener for announcements
   */
  const initializeAnnouncementsListener = async () => {
    if (isListenerActive.value) return
    // Skip persistent webchannel streaming in headless prerendering (Puppeteer)
    if (typeof navigator !== 'undefined' && navigator.webdriver) {
      isLoadingAnnouncements.value = false
      return
    }
    isListenerActive.value = true
    isLoadingAnnouncements.value = true

    try {
      const [{ doc, onSnapshot }, { db }] = await Promise.all([
        import('firebase/firestore'),
        import('../firebase')
      ])

      const announcementsDocRef = doc(db, 'system_config', 'announcements')

      unsubscribeSnapshot = onSnapshot(
        announcementsDocRef,
        (snapshot) => {
          isLoadingAnnouncements.value = false
          if (snapshot.exists()) {
            const data = snapshot.data()
            const items = Array.isArray(data.items) ? data.items : []
            announcements.value = items
            cleanupOrphanedDismissals(items)
          } else {
            announcements.value = []
            cleanupOrphanedDismissals([])
          }
        },
        (error) => {
          isLoadingAnnouncements.value = false
          console.warn('[useAnnouncements] Firestore listener error:', error.message)
        }
      )
    } catch (err) {
      isLoadingAnnouncements.value = false
      console.warn('[useAnnouncements] Failed to init announcements listener:', err)
    }
  }

  /**
   * Admin: Saves the full announcements list to Firestore
   */
  const saveAnnouncementsToFirestore = async (newItems: SystemAnnouncement[]) => {
    const [{ doc, setDoc }, { db }] = await Promise.all([
      import('firebase/firestore'),
      import('../firebase')
    ])

    const sanitizedItems = newItems.map(sanitizeAnnouncement)

    const announcementsDocRef = doc(db, 'system_config', 'announcements')
    await setDoc(announcementsDocRef, {
      items: sanitizedItems,
      updatedAt: Date.now()
    }, { merge: true })

    announcements.value = sanitizedItems
    cleanupOrphanedDismissals(sanitizedItems)
  }

  /**
   * Admin: Adds or updates an announcement
   */
  const saveAnnouncement = async (
    itemData: Omit<SystemAnnouncement, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }
  ) => {
    const now = Date.now()
    const id = itemData.id || `announcement_${now}_${Math.random().toString(36).substring(2, 7)}`
    const isEdit = announcements.value.some((a) => a.id === id)

    let updatedList: SystemAnnouncement[] = []

    if (isEdit) {
      updatedList = announcements.value.map((a) => {
        if (a.id === id) {
          return sanitizeAnnouncement({
            ...a,
            ...itemData,
            id,
            updatedAt: now
          })
        }
        return a
      })
    } else {
      const newItem: SystemAnnouncement = sanitizeAnnouncement({
        ...itemData,
        id,
        createdAt: now,
        updatedAt: now
      })
      updatedList = [newItem, ...announcements.value]
    }

    await saveAnnouncementsToFirestore(updatedList)
    return id
  }

  /**
   * Admin: Deletes an announcement
   */
  const deleteAnnouncement = async (id: string) => {
    undismissAnnouncement(id)
    const updatedList = announcements.value.filter((a) => a.id !== id)
    await saveAnnouncementsToFirestore(updatedList)
  }

  /**
   * Admin: Toggles active status of an announcement
   */
  const toggleAnnouncementActive = async (id: string, active: boolean) => {
    const updatedList = announcements.value.map((a) => {
      if (a.id === id) {
        return sanitizeAnnouncement({ ...a, active, updatedAt: Date.now() })
      }
      return a
    })
    await saveAnnouncementsToFirestore(updatedList)
  }

  return {
    announcements,
    activeAnnouncements,
    currentBannerAnnouncement,
    dismissedIds,
    dismissedTimestamps,
    isLoadingAnnouncements,
    dismissAnnouncement,
    undismissAnnouncement,
    initializeAnnouncementsListener,
    saveAnnouncement,
    deleteAnnouncement,
    toggleAnnouncementActive,
    saveAnnouncementsToFirestore,
    unsubscribeAnnouncements
  }
}
